import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, getSavedRestaurantCode, refreshPushSubscription } from '@/lib/auth';
import { api } from '@/lib/api';
import { Bike, Hash, Phone, KeyRound, ChevronDown } from 'lucide-react';

export default function DriverLogin() {
  const { session, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && session?.role === 'driver') {
      navigate('/driver', { replace: true });
    }
  }, [session, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center" dir="rtl">
        <div className="text-center animate-fade-in">
          <Bike className="w-12 h-12 text-cyan-400 mx-auto mb-3 animate-pulse" />
          <p className="text-navy-300">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (session?.role === 'driver') return null;

  return <LoginForm />;
}

function LoginForm() {
  const savedCode = getSavedRestaurantCode();
  const [restaurantCode, setRestaurantCode] = useState(savedCode || '');
  const [showCodeField, setShowCodeField] = useState(!savedCode);
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { setDriverSession } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await api.auth.driverLogin({
        restaurant_code: restaurantCode.toUpperCase().trim(),
        phone: phone.trim(),
        pin: pin.trim(),
      });
      setDriverSession(data);
      refreshPushSubscription(data.token);
      navigate('/driver', { replace: true });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800 flex items-center justify-center p-4" dir="rtl">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-cyan-700/20 rounded-3xl mb-4 border border-cyan-700/30">
            <Bike className="w-10 h-10 text-cyan-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">Vendox Delivery</h1>
          <p className="text-navy-300 mt-2">تسجيل دخول السائق</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-navy-800/60 backdrop-blur-lg border border-navy-700 rounded-3xl p-6 space-y-5">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-xl p-3 text-center">
              {error}
            </div>
          )}

          {showCodeField ? (
            <div className="space-y-1 animate-slide-up">
              <label className="text-sm text-navy-200">رمز المطعم</label>
              <div className="relative">
                <Hash className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
                <input
                  type="text"
                  value={restaurantCode}
                  onChange={e => setRestaurantCode(e.target.value)}
                  className="w-full bg-navy-900/80 border border-navy-600 rounded-xl pr-10 pl-4 py-3.5 text-white placeholder-navy-500 focus:outline-none focus:border-cyan-500 transition-colors font-mono uppercase tracking-wider"
                  placeholder="RST-XXXXXX"
                  required
                  dir="ltr"
                />
              </div>
            </div>
          ) : (
            <div className="bg-navy-900/50 rounded-xl px-4 py-2.5 flex items-center justify-between">
              <div>
                <p className="text-navy-400 text-xs">المطعم</p>
                <p className="text-cyan-400 font-mono text-sm" dir="ltr">{restaurantCode}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowCodeField(true)}
                className="text-navy-500 hover:text-cyan-400 transition-colors text-xs flex items-center gap-1"
              >
                <ChevronDown className="w-3 h-3" />
                تغيير
              </button>
            </div>
          )}

          <div className="space-y-1">
            <label className="text-sm text-navy-200">رقم الهاتف</label>
            <div className="relative">
              <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                className="w-full bg-navy-900/80 border border-navy-600 rounded-xl pr-10 pl-4 py-3.5 text-white placeholder-navy-500 focus:outline-none focus:border-cyan-500 transition-colors"
                placeholder="05XXXXXXXX"
                required
                dir="ltr"
                autoFocus={!showCodeField}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm text-navy-200">رمز PIN</label>
            <div className="relative">
              <KeyRound className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
              <input
                type="password"
                value={pin}
                onChange={e => setPin(e.target.value)}
                maxLength={4}
                inputMode="numeric"
                pattern="[0-9]{4}"
                className="w-full bg-navy-900/80 border border-navy-600 rounded-xl pr-10 pl-4 py-3.5 text-white placeholder-navy-500 focus:outline-none focus:border-cyan-500 transition-colors font-mono text-center text-xl tracking-[0.5em]"
                placeholder="• • • •"
                required
                dir="ltr"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-l from-cyan-600 to-cyan-700 hover:from-cyan-500 hover:to-cyan-600 disabled:opacity-50 text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-cyan-900/30"
          >
            {loading ? 'جاري الدخول...' : 'دخول'}
          </button>
        </form>
      </div>
    </div>
  );
}
